import React, {useState, useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';
import {RootState} from '../store';

import {text} from '../text';
import {hooks} from '../hooks';
import {custom} from '../custom';
import {theme} from '../constants';
import {components} from '../components';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { compressImage } from '../utils/imageUtils';
import { getCurrentLocation } from '../utils/locationUtils';
import { MdTitle, MdDescription, MdLocationOn, MdMap, MdCalendarMonth, MdAttachFile, MdDelete, MdCheckCircle, MdError, MdWarning } from 'react-icons/md';

// Icon Assignments
const TitleIcon = MdTitle as any;
const DescriptionIcon = MdDescription as any;
const LocationIcon = MdLocationOn as any;
const MapIcon = MdMap as any;
const CalendarIcon = MdCalendarMonth as any;
const AttachFileIcon = MdAttachFile as any;
const DeleteIcon = MdDelete as any;
const CheckCircleIcon = MdCheckCircle as any;
const ErrorIcon = MdError as any;
const WarningIcon = MdWarning as any;

export const CreateActivity: React.FC = () => {
  const {pathname} = useLocation();
  const navigate = hooks.useAppNavigate();
  const {authSlice} = hooks.useAppSelector((state: RootState) => state);
  const { t } = useLanguage();

  const getDefaultDateTime = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offset).toISOString().slice(0, 16);
  };

  // Form States
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [datetime, setDatetime] = useState<string>(getDefaultDateTime());
  const [location, setLocation] = useState<string>(''); // Akan menyimpan "lat,lng"
  const [address, setAddress] = useState<string>('');
  const [locationError, setLocationError] = useState<string>('');
  const [isFetchingLocation, setIsFetchingLocation] = useState<boolean>(true);
  const [attachments, setAttachments] = useState<File[]>([]);
  
  const [loading, setLoading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning';
    onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  useEffect(() => {
    setTimeout(() => {
      window.scroll({top: -1, left: 0, behavior: 'smooth'});
    }, 10);
  }, [pathname]);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const locationData = await getCurrentLocation({ addressFormat: 'short' });
        setLocation(`${locationData.lat},${locationData.lng}`);
        setAddress(locationData.address || 'Address not found');
      } catch (error: any) {
        setLocationError(error.message || 'Failed to get location. Please enable GPS and allow permission.');
      } finally {
        setIsFetchingLocation(false);
      }
    };

    fetchLocation();
  }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const processedFiles = await Promise.all(newFiles.map(async (file) => {
        if (file.type.startsWith('image/')) {
          try {
            return await compressImage(file);
          } catch (error) {
            console.error("Compression error:", error);
            return file;
          }
        }
        return file;
      }));
      setAttachments(prev => [...prev, ...processedFiles]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' = 'success', onClose?: () => void) => {
    setAlertConfig({
      isOpen: true,
      title,
      message,
      type,
      onClose,
    });
  };

  const hideAlert = () => {
    const onClose = alertConfig.onClose;
    setAlertConfig((prev) => ({ ...prev, isOpen: false }));
    if (onClose) {
      onClose();
    }
  };

  const handleCreateActivity = async () => {
    if (!title || !datetime) {
      showAlert(t.common?.warning || "Warning", t.activity?.validation?.fillRequired || "Title and DateTime are required.", 'warning');
      return;
    }

    if (!location) {
      showAlert(t.common?.warning || "Warning", locationError || "Fetching location...", 'warning');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('datetime', new Date(datetime).toISOString());
      if (description) formData.append('description', description);
      if (location) formData.append('location', location);
      if (address) formData.append('address', address);
      attachments.forEach(file => {
        formData.append('attachments', file);
      });

      await axiosInstance.post('/activities', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      showAlert(
        t.common?.success || "Success", 
        t.activity?.alerts?.success || "Activity created successfully!", 
        'success', 
        () => {
          navigate(-1); // Kembali ke halaman list activity
        }
      );
    } catch (error) {
      console.error('Failed to create activity:', error);
      showAlert(t.common?.error || "Error", t.activity?.alerts?.failed || "Failed to create activity. Please try again.", 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderBackground = (): JSX.Element => {
    return <components.Background />;
  };

  const renderHeader = (): JSX.Element => {
    return (
      <components.Header
        title={t.activity?.createTitle || "Create Activity"}
        goBack={true}
      />
    );
  };

  const renderContent = (): JSX.Element => {
    return (
      <main
        style={{marginTop: 62, paddingBottom: 40}}
        className='container'
      >
        {/* Title Input */}
        <div style={{marginBottom: 16}}>
          <text.H5 style={{color: theme.colors.mainDark, marginBottom: 10}}>Title *</text.H5>
          <custom.InputField
            placeholder={t.activity?.titlePlaceholder || "Enter activity title"}
            value={title}
            onChange={(e: any) => setTitle(e.target.value)}
            leftIcon={<TitleIcon />}
            containerStyle={{marginBottom: 0}}
          />
        </div>

        {/* DateTime Input */}
        <div style={{marginBottom: 16}}>
          <text.H5 style={{color: theme.colors.mainDark, marginBottom: 10}}>Date & Time *</text.H5>
          <custom.InputField
            placeholder={t.activity?.datetimePlaceholder || "Select Date and Time"}
            value={datetime}
            onChange={(e: any) => setDatetime(e.target.value)}
            leftIcon={<CalendarIcon />}
            type={"datetime-local" as any}
            containerStyle={{marginBottom: 0}}
          />
        </div>

        {/* Location Display */}
        <div style={{marginBottom: 24}}>
          <text.H5 style={{color: theme.colors.mainDark, marginBottom: 10}}>{t.common.location}</text.H5>
          <div style={{
            padding: 16,
            backgroundColor: '#F9FAFB',
            borderRadius: 10,
            border: '1px solid #F3F4F6',
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <LocationIcon size={24} color={theme.colors.mainColor} />
            <div>
              {isFetchingLocation ? (
                <text.T14 style={{color: '#9CA3AF', fontStyle: 'italic'}}>{t.taskSubmission.fetchingLocation}</text.T14>
              ) : locationError ? (
                <text.T14 style={{color: '#EF4444'}}>{locationError}</text.T14>
              ) : (
                <>
                  <text.H6 style={{color: theme.colors.mainDark}}>{address || 'Location acquired'}</text.H6>
                  <text.T14 style={{color: '#6B7280', fontSize: 12, marginTop: 2}}>{location}</text.T14>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Description Textarea */}
        <div style={{marginBottom: 16}}>
          <text.H5 style={{color: theme.colors.mainDark, marginBottom: 10}}>{t.common.description}</text.H5>
          <div
            style={{
              width: '100%',
              padding: '14px 20px',
              backgroundColor: theme.colors.white,
              border: '1px solid #FFEFE6',
              borderRadius: 10,
            }}
          >
            <textarea
              placeholder={t.activity?.descriptionPlaceholder || "Write a detailed description of the activity..."}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{
                width: '100%',
                height: 120,
                ...theme.fonts.SourceSansPro_400Regular,
                outline: 'none',
                border: 'none',
                resize: 'none',
                whiteSpace: 'pre-wrap'
              }}
            />
          </div>
        </div>

        {/* Attachment Upload */}
        <div style={{marginBottom: 16}}>
          <text.H5 style={{color: theme.colors.mainDark, marginBottom: 10}}>{t.common.attachments}</text.H5>
          
          <div style={{display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 10}}>
            {attachments.map((file, index) => (
              <div key={index} style={{ position: 'relative', width: 100, height: 100, flexShrink: 0, borderRadius: 10, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                {file.type.startsWith('image/') ? (
                  <img src={URL.createObjectURL(file)} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', backgroundColor: '#F9FAFB' }}>
                    <AttachFileIcon size={32} color={theme.colors.mainColor} />
                  </div>
                )}
                <div onClick={() => removeAttachment(index)} style={{ position: 'absolute', top: 4, right: 4, cursor: 'pointer', padding: 4, backgroundColor: 'rgba(255,255,255,0.8)', borderRadius: '50%' }}>
                  <DeleteIcon size={16} color="#EF4444" />
                </div>
              </div>
            ))}
            
            <div 
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: 100, height: 100, flexShrink: 0,
                border: '1px dashed #9CA3AF', borderRadius: 10, 
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                backgroundColor: '#F9FAFB', cursor: 'pointer'
              }}
            >
              <AttachFileIcon size={24} color="#9CA3AF" />
              <text.T14 style={{marginTop: 4, color: '#6B7280', fontSize: 12, textAlign: 'center'}}>Add</text.T14>
            </div>
          </div>

          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileChange} 
            style={{display: 'none'}} 
            accept="image/*,.pdf,.doc,.docx"
            multiple
          />
        </div>
      </main>
    );
  };

  const renderButton = (): JSX.Element => {
    return (
      <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          backgroundColor: 'white', padding: '16px 24px 32px',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.05)',
          zIndex: 10
      }}>
          <div className='container' style={{padding: 0, maxWidth: 650, margin: '0 auto'}}>
              <components.Button
                title={loading ? (t.common?.saving || "Saving...") : (t.common?.submit || "Submit Activity")}
                onClick={handleCreateActivity}
              />
          </div>
      </div>
    );
  };

  const renderAlert = () => {
    if (!alertConfig.isOpen) return null;
    return (
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000,
          display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
        }}
      >
        <div
          style={{
            backgroundColor: 'white', borderRadius: 16, padding: 24,
            width: '100%', maxWidth: 320, textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            display: 'flex', flexDirection: 'column', alignItems: 'center'
          }}
        >
          <div style={{marginBottom: 16}}>
            {alertConfig.type === 'success' && <CheckCircleIcon size={60} color="#10B981" />}
            {alertConfig.type === 'error' && <ErrorIcon size={60} color="#EF4444" />}
            {alertConfig.type === 'warning' && <WarningIcon size={60} color="#F59E0B" />}
          </div>
          <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
          <text.T16 style={{ marginBottom: 24, color: theme.colors.bodyTextColor, lineHeight: 1.5 }}>
            {alertConfig.message}
          </text.T16>
          <components.Button title={t.common?.ok || "OK"} onClick={hideAlert} containerStyle={{width: '100%'}} />
        </div>
      </div>
    );
  };

  return (
    <div id='screen'>
      {renderBackground()}
      {renderHeader()}
      <div style={{padding: 0}}>
        {renderContent()}
      </div>
      {renderButton()}
      {renderAlert()}
    </div>
  );
};